import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import apiClient, { API_ENDPOINTS } from '../utils/api';

function Dashboard() {
  const [stats, setStats] = useState({
    totalBookings: 0,
    activeBookings: 0,
    totalRevenue: 0,
    pendingEmails: 0
  });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      const [bookingsRes, emailsRes] = await Promise.all([
        apiClient.get(API_ENDPOINTS.BOOKINGS),
        apiClient.get(API_ENDPOINTS.EMAILS)
      ]);

      const bookings = bookingsRes.data;
      const emails = emailsRes.data;
      const today = new Date();
      const monthBuckets = {};

      bookings.forEach((booking) => {
        const checkIn = new Date(booking.check_in_date);
        const month = `${checkIn.getMonth() + 1}月`;
        if (!monthBuckets[month]) {
          monthBuckets[month] = { name: month, 予約数: 0, 売上: 0 };
        }
        monthBuckets[month].予約数 += 1;
        monthBuckets[month].売上 += Number(booking.total_price || 0);
      });

      const activeBookings = bookings.filter((booking) => {
        const checkIn = new Date(booking.check_in_date);
        const checkOut = new Date(booking.check_out_date);
        return checkIn <= today && today < checkOut && booking.booking_status !== 'cancelled';
      }).length;

      setStats({
        totalBookings: bookings.length,
        activeBookings,
        totalRevenue: bookings.reduce((sum, booking) => sum + Number(booking.total_price || 0), 0),
        pendingEmails: emails.filter((email) => email.status === 'pending').length
      });

      setChartData(Object.values(monthBuckets));
    } catch (err) {
      setError(err.response?.data?.detail || 'ダッシュボードデータの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid>
      <div className="page-header">
        <h1 className="page-title">ダッシュボード</h1>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Row className="mb-4">
        <Col md={6} lg={3}>
          <div className="dashboard-stat">
            <div className="stat-value">{stats.totalBookings}</div>
            <div className="stat-label">総予約数</div>
          </div>
        </Col>
        <Col md={6} lg={3}>
          <div className="dashboard-stat">
            <div className="stat-value" style={{ color: '#00a699' }}>{stats.activeBookings}</div>
            <div className="stat-label">現在のチェックイン</div>
          </div>
        </Col>
        <Col md={6} lg={3}>
          <div className="dashboard-stat">
            <div className="stat-value" style={{ color: '#417505' }}>¥{(stats.totalRevenue / 10000).toFixed(0)}万</div>
            <div className="stat-label">総売上</div>
          </div>
        </Col>
        <Col md={6} lg={3}>
          <div className="dashboard-stat">
            <div className="stat-value" style={{ color: '#ff9000' }}>{stats.pendingEmails}</div>
            <div className="stat-label">未処理メール</div>
          </div>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col lg={6}>
          <Card>
            <Card.Header>月別予約数</Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="予約数" fill="#ff5a5f" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card>
            <Card.Header>月別売上</Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="売上" stroke="#ff5a5f" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Dashboard;
